import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useToggle } from '@/hooks/use-toggle';

import { ForgotPassword } from './components/forgot-password';
import { LoginForm } from './components/login-form';

import { useAuthStore } from '@/store/auth.store';

export default function LoginPage() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const redirectPath = useAuthStore(state => state.redirectPath);
  const navigate = useNavigate();
  const location = useLocation();

  const { state: showForgotPassword, toggle } = useToggle();

  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo =
        (location.state && location.state.from && location.state.from.pathname) || redirectPath;
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate, redirectPath]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <a href="/" className="flex items-center gap-2 no-underline" aria-label="LIKA Home">
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g className="fill-primary">
                <path
                  d="M22.1667 0H5.83333C2.61167 0 0 2.61167 0 5.83333V22.1667C0 25.3883 2.61167 28 5.83333 28H22.1667C25.3883 28 28 25.3883 28 22.1667V5.83333C28 2.61167 25.3883 0 22.1667 0Z"
                  fill=""
                />
                <path
                  className="stroke-primary-foreground"
                  d="M22.1006 6.20236V7.62555M22.8121 6.91396H21.3889M16.4078 11.1836V11.8952M16.7635 11.5394H16.052M17.1527 21.8761V24.011C17.1536 24.2092 17.113 24.4054 17.0336 24.5869C16.9541 24.7686 16.8377 24.9315 16.6917 25.0655C16.5457 25.1994 16.3732 25.3015 16.1855 25.3649C15.9977 25.4285 15.7988 25.452 15.6014 25.4342C13.4116 25.1962 11.3082 24.4481 9.46014 23.2495C7.74075 22.157 6.28301 20.6992 5.19044 18.9799C3.9878 17.1234 3.23936 15.0098 3.00577 12.8101C2.98799 12.6134 3.01138 12.415 3.07444 12.2278C3.13752 12.0405 3.23889 11.8685 3.37209 11.7225C3.5053 11.5766 3.66744 11.46 3.84818 11.3802C4.02893 11.3003 4.2243 11.259 4.4219 11.2588H6.55675C6.9021 11.2554 7.2369 11.3777 7.49876 11.6029C7.76062 11.8281 7.93165 12.1408 7.97999 12.4828C8.07009 13.166 8.23719 13.8368 8.47812 14.4825C8.57386 14.7371 8.59458 15.0139 8.53782 15.2801C8.48107 15.5462 8.34921 15.7905 8.15789 15.9839L7.25413 16.8877C8.26715 18.6692 9.74226 20.1443 11.5238 21.1574L12.4276 20.2537C12.6211 20.0624 12.8653 19.9304 13.1315 19.8737C13.3976 19.8169 13.6744 19.8377 13.9291 19.9335C14.5747 20.1743 15.2455 20.3415 15.9288 20.4315C16.2745 20.4803 16.5901 20.6544 16.8158 20.9208C17.0415 21.1871 17.1614 21.5272 17.1527 21.8761ZM16.3371 15.1031C17.6953 15.7997 19.2577 15.9885 20.7427 15.6353C22.2277 15.2819 23.5376 14.4098 24.4366 13.1762C25.3355 11.9425 25.7642 10.4283 25.6455 8.90644C25.5268 7.3846 24.8685 5.95522 23.7891 4.87586C22.7097 3.79649 21.2803 3.13813 19.7584 3.01941C18.2367 2.90068 16.7224 3.3294 15.4888 4.22832C14.2551 5.12724 13.383 6.43723 13.0297 7.92224C12.6764 9.40724 12.8651 10.9696 13.5619 12.3278L12.1386 16.5263L16.3371 15.1031ZM18.5202 10.6499C18.4884 10.5267 18.4242 10.4144 18.3343 10.3244C18.2444 10.2345 18.1319 10.1703 18.0088 10.1386L15.826 9.57569C15.7888 9.56512 15.756 9.54269 15.7327 9.51181C15.7094 9.48093 15.6967 9.44327 15.6967 9.40455C15.6967 9.36584 15.7094 9.32818 15.7327 9.2973C15.756 9.26641 15.7888 9.24399 15.826 9.23342L18.0088 8.67019C18.1319 8.63845 18.2443 8.57433 18.3342 8.48447C18.4241 8.39461 18.4883 8.28232 18.5202 8.15926L19.083 5.97642C19.0935 5.93903 19.1159 5.9061 19.1468 5.88263C19.1778 5.85917 19.2155 5.84647 19.2543 5.84647C19.2932 5.84647 19.3309 5.85917 19.3619 5.88263C19.3927 5.9061 19.4152 5.93903 19.4257 5.97642L19.9881 8.15926C20.0199 8.28239 20.0842 8.39476 20.1741 8.48468C20.2639 8.5746 20.3764 8.63877 20.4994 8.67054L22.6822 9.23306C22.7198 9.24341 22.753 9.2658 22.7765 9.29677C22.8001 9.32776 22.8129 9.36562 22.8129 9.40455C22.8129 9.44349 22.8001 9.48135 22.7765 9.51234C22.753 9.54331 22.7198 9.5657 22.6822 9.57605L20.4994 10.1386C20.3764 10.1703 20.2639 10.2345 20.1741 10.3244C20.0842 10.4144 20.0199 10.5267 19.9881 10.6499L19.4253 12.8327C19.4148 12.8701 19.3925 12.903 19.3615 12.9265C19.3306 12.9499 19.2928 12.9626 19.254 12.9626C19.2152 12.9626 19.1774 12.9499 19.1464 12.9265C19.1155 12.903 19.0931 12.8701 19.0826 12.8327L18.5202 10.6499Z"
                  stroke="white"
                  strokeWidth={1.16667}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
            <span className="font-semibold text-lg text-foreground">LIKA</span>
          </a>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            {showForgotPassword ? (
              <ForgotPassword toggle={toggle} />
            ) : (
              <LoginForm toggle={toggle} />
            )}
          </div>
        </div>
      </div>
      <div className="relative hidden lg:flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-primary/90 via-primary to-primary/70">
        {/* Ambient blur blobs */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />

        {/* Main illustration */}
        <div className="relative z-10 flex flex-col items-center gap-8 px-12 text-center">
          {/* Voice AI SVG illustration */}
          <svg
            width="280"
            height="280"
            viewBox="0 0 280 280"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Voice AI assistant illustration"
          >
            {/* Outer pulse rings */}
            <circle cx="140" cy="140" r="130" stroke="white" strokeOpacity="0.08" strokeWidth="1" />
            <circle cx="140" cy="140" r="110" stroke="white" strokeOpacity="0.12" strokeWidth="1" />
            <circle cx="140" cy="140" r="90" stroke="white" strokeOpacity="0.18" strokeWidth="1" />

            {/* Centre card */}
            <rect x="90" y="90" width="100" height="100" rx="28" fill="white" fillOpacity="0.15" />
            <rect
              x="90"
              y="90"
              width="100"
              height="100"
              rx="28"
              stroke="white"
              strokeOpacity="0.35"
              strokeWidth="1.5"
            />

            {/* Microphone body */}
            <rect x="128" y="108" width="24" height="36" rx="12" fill="white" />

            {/* Microphone stand */}
            <path
              d="M118 148 C118 162 162 162 162 148"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
            <line
              x1="140"
              y1="162"
              x2="140"
              y2="172"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <line
              x1="132"
              y1="172"
              x2="148"
              y2="172"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
            />

            {/* Sound waves — left */}
            <path
              d="M108 132 C103 136 103 144 108 148"
              stroke="white"
              strokeOpacity="0.9"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M100 126 C92  132 92  148 100 154"
              stroke="white"
              strokeOpacity="0.6"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M92  120 C81  128 81  152 92  160"
              stroke="white"
              strokeOpacity="0.35"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
            />

            {/* Sound waves — right */}
            <path
              d="M172 132 C177 136 177 144 172 148"
              stroke="white"
              strokeOpacity="0.9"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M180 126 C188 132 188 148 180 154"
              stroke="white"
              strokeOpacity="0.6"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M188 120 C199 128 199 152 188 160"
              stroke="white"
              strokeOpacity="0.35"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
            />

            {/* Floating sparkle dots */}
            <circle cx="60" cy="80" r="4" fill="white" fillOpacity="0.5" />
            <circle cx="220" cy="70" r="3" fill="white" fillOpacity="0.4" />
            <circle cx="50" cy="200" r="3" fill="white" fillOpacity="0.35" />
            <circle cx="230" cy="210" r="4" fill="white" fillOpacity="0.45" />
            <circle cx="140" cy="38" r="3" fill="white" fillOpacity="0.4" />
            <circle cx="140" cy="242" r="3" fill="white" fillOpacity="0.4" />

            {/* AI sparkle — top-right of card */}
            <path
              d="M202 88 L204 82 L206 88 L212 90 L206 92 L204 98 L202 92 L196 90 Z"
              fill="white"
              fillOpacity="0.8"
            />
          </svg>

          {/* Text */}
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-white leading-tight">
              AI-powered voice
              <br />
              assistant platform
            </h2>
            <p className="text-sm text-white/70 max-w-xs leading-relaxed">
              Automate patient calls, schedule appointments, and deliver personalised care — at
              scale.
            </p>
          </div>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-2">
            {['Real-time transcription', 'Smart scheduling', 'Post-call analysis'].map(feat => (
              <span
                key={feat}
                className="px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white/90 text-xs font-medium"
              >
                {feat}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
